import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from 'store';
import { fetchCreditBalance } from 'store/slices/creditsSlice';
import './CreditConfirmPopup.css';

export interface CreditConfirmPopupProps {
  isOpen: boolean;
  moduleKey: string;
  moduleName: string;
  featureKey?: string;
  creditCost?: number;
  actionDescription?: string;
  onConfirm: () => void;
  onCancel: () => void;
  onNeverShowAgain?: () => void;
}

const STORAGE_KEY = 'credit_popup_suppressed_modules';
const RESET_FLAG = 'credit_popup_suppressed_reset_v1';

/**
 * One-time cleanup: older builds pre-checked the "Don't show again" checkbox,
 * which silently suppressed popups for users who just clicked Confirm.
 * Clears any stored suppressions once so those users see the popup again.
 */
export const resetCreditPopupSuppressions = (): void => {
  try {
    if (localStorage.getItem(RESET_FLAG)) return;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem(RESET_FLAG, '1');
  } catch { }
};

export const isCreditPopupSuppressed = (moduleKey: string): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as string[]).includes(moduleKey);
  } catch { return false; }
};

export const suppressCreditPopup = (moduleKey: string): void => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(moduleKey)) {
      list.push(moduleKey);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    }
  } catch { }
};

export const unsuppressCreditPopup = (moduleKey: string): void => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(
      (JSON.parse(raw) as string[]).filter(k => k !== moduleKey)
    ));
  } catch { }
};

const CreditConfirmPopup: React.FC<CreditConfirmPopupProps> = ({
  isOpen, moduleKey, moduleName, featureKey, creditCost,
  actionDescription, onConfirm, onCancel, onNeverShowAgain,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const reduxBalance = useSelector((state: RootState) => state.credits.balance);
  const isReduxLoading = useSelector((state: RootState) => state.credits.isLoading);

  // Opt-in only — must never default to true, otherwise a single
  // "Confirm" click silently suppresses the popup forever.
  const [neverShow, setNeverShow] = useState(false);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  const resolvedCost = creditCost !== undefined ? creditCost : 0;
  const availableCredits = reduxBalance?.availableCredits ?? (reduxBalance as any)?.available_credits ?? (reduxBalance as any)?.credits_balance ?? (reduxBalance as any)?.balance ?? 0;
  const hasEnough = availableCredits >= resolvedCost;
  const shortage = resolvedCost - availableCredits;
  const afterBalance = hasEnough ? availableCredits - resolvedCost : 0;
  const isLoading = isReduxLoading && !reduxBalance;

  useEffect(() => {
    if (isOpen) {
      setNeverShow(false);
      setTimeout(() => confirmBtnRef.current?.focus(), 80);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const fmt = (n: number) => n.toLocaleString();

  const handleConfirmClick = () => {
    if (neverShow) { suppressCreditPopup(moduleKey); onNeverShowAgain?.(); }
    onConfirm();
  };

  return (
    <div className="ccp3-overlay" onClick={onCancel} role="dialog" aria-modal="true" aria-labelledby="ccp3-title">
      <style>{`
        @keyframes ccp3-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ccp3-scale-in {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .ccp3-overlay {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          animation: ccp3-fade-in 0.2s ease-out forwards;
        }
        .ccp3-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          padding: 28px 24px 24px;
          animation: ccp3-scale-in 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          position: relative;
          border: 1.5px solid #fde68a;
          // border-top: 6px solid #d97706;
        }
        .ccp3-close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 28px;
          height: 28px;
          border: none;
          background: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: #94a3b8;
          transition: background 0.15s, color 0.15s;
        }
        .ccp3-close:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .ccp3-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          margin-bottom: 20px;
        }
        .ccp3-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #fff3c5;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          color: #d97706;
          border: 1px solid #ffcc00;
        }
        .ccp3-coin-icon {
          width: 24px;
          height: 24px;
        }
        .ccp3-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 2px 0;
        }
        .ccp3-subtitle {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          margin: 0;
        }
        .ccp3-body {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
        }
        .ccp3-desc {
          font-size: 14px;
          color: #475569;
          text-align: center;
          line-height: 1.5;
          margin: 0;
        }
        .ccp3-preview-card {
          display: flex;
          background: #fff3c5;
          border: 1px solid #ffcc00;
          border-radius: 12px;
          padding: 12px 16px;
          justify-content: space-around;
          align-items: center;
        }
        .ccp3-preview-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .ccp3-preview-label {
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        .ccp3-preview-value {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
        }
        .ccp3-cost {
          color: #ef4444;
        }
        .ccp3-remaining {
          color: #10b981;
        }
        .ccp3-preview-divider {
          width: 1px;
          height: 24px;
          background: #e2e8f0;
        }
        .ccp3-error-alert {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fef2f2;
          border: 1px solid #fee2e2;
          border-radius: 8px;
          padding: 10px 12px;
          color: #991b1b;
          font-size: 13px;
        }
        .ccp3-error-icon {
          width: 16px;
          height: 16px;
          color: #ef4444;
          flex-shrink: 0;
        }
        .ccp3-checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
          font-size: 13px;
          color: #64748b;
          margin: 4px auto 0 auto;
          width: fit-content;
        }
        .ccp3-checkbox-input {
          display: none;
        }
        .ccp3-checkbox-custom {
          width: 16px;
          height: 16px;
          border: 1.5px solid #cbd5e1;
          border-radius: 4px;
          display: inline-block;
          position: relative;
          transition: all 0.15s;
          background: #ffffff;
        }
        .ccp3-checkbox-input:checked + .ccp3-checkbox-custom {
          background: #2563eb;
          border-color: #2563eb;
        }
        .ccp3-checkbox-input:checked + .ccp3-checkbox-custom::after {
          content: "";
          position: absolute;
          left: 5px;
          top: 2px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        .ccp3-checkbox-label:hover .ccp3-checkbox-custom {
          border-color: #94a3b8;
        }
        .ccp3-actions {
          display: flex;
          gap: 12px;
        }
        .ccp3-btn {
          flex: 1;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          padding: 10px 16px;
          text-align: center;
          white-space: nowrap;
        }
        .ccp3-btn-cancel {
          background: #f1f5f9;
          color: #475569;
        }
        .ccp3-btn-cancel:hover {
          background: #e2e8f0;
        }
        .ccp3-btn-confirm {
          background: #002781;
          color: #ffffff;
          box-shadow: 0 4px 10px rgba(0, 39, 129, 0.2);
        }
        .ccp3-btn-confirm:hover:not(:disabled) {
          background: #0b3499;
          box-shadow: 0 6px 14px rgba(0, 39, 129, 0.3);
        }
        .ccp3-btn-confirm:disabled {
          background: #cbd5e1;
          color: #94a3b8;
          box-shadow: none;
          cursor: not-allowed;
        }
      `}</style>

      <div className="ccp3-card" onClick={e => e.stopPropagation()}>
        {/* Close */}
        <button className="ccp3-close" onClick={onCancel} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <div className="ccp3-header">
          <div className="ccp3-icon-wrapper">
           <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M9.5 13.75C9.5 14.72 10.25 15.5 11.17 15.5H13.05C13.85 15.5 14.5 14.82 14.5 13.97C14.5 13.06 14.1 12.73 13.51 12.52L10.5 11.47C9.91 11.26 9.51001 10.94 9.51001 10.02C9.51001 9.17999 10.16 8.48999 10.96 8.48999H12.84C13.76 8.48999 14.51 9.26999 14.51 10.24" stroke="#d97706" stroke-width="1.464" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M12 7.5V16.5" stroke="#d97706" stroke-width="1.464" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2" stroke="#d97706" stroke-width="1.464" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M22 6V2H18" stroke="#d97706" stroke-width="1.464" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M17 7L22 2" stroke="#d97706" stroke-width="1.464" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          </div>
          <h2 className="ccp3-title" id="ccp3-title">Confirm Credit Deduction</h2>
          <p className="ccp3-subtitle">{moduleName}</p>
        </div>

        {/* Body */}
        <div className="ccp3-body">
          <p className="ccp3-desc">
            {actionDescription || 'Performing this action will deduct credits from your balance.'}
          </p>

          {/* Balance Preview Card */}
          <div className="ccp3-preview-card">
            <div className="ccp3-preview-item">
              <span className="ccp3-preview-label">Cost</span>
              <span className="ccp3-preview-value ccp3-cost">-{resolvedCost}</span>
            </div>
            <div className="ccp3-preview-divider" />
            <div className="ccp3-preview-item">
              <span className="ccp3-preview-label">Balance</span>
              <span className="ccp3-preview-value">
                {isLoading && !reduxBalance ? '...' : fmt(availableCredits)}
              </span>
            </div>
            {hasEnough && (
              <>
                <div className="ccp3-preview-divider" />
                <div className="ccp3-preview-item">
                  <span className="ccp3-preview-label">Remaining</span>
                  <span className="ccp3-preview-value ccp3-remaining">
                    {isLoading && !reduxBalance ? '...' : fmt(afterBalance)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Insufficient Warning */}
          {!hasEnough && (
            <div className="ccp3-error-alert">
              <svg className="ccp3-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                Insufficient credits. You need <strong>{fmt(shortage)}</strong> more.
              </span>
            </div>
          )}

          {/* Never Show Again Checkbox */}
          <label className="ccp3-checkbox-label">
            <input
              type="checkbox"
              checked={neverShow}
              onChange={e => setNeverShow(e.target.checked)}
              className="ccp3-checkbox-input"
            />
            <span className="ccp3-checkbox-custom" />
            <span className="ccp3-checkbox-text">Don't show this confirmation again</span>
          </label>
        </div>

        {/* Buttons */}
        <div className="ccp3-actions">
          <button className="ccp3-btn ccp3-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          {
            hasEnough
            ?<button ref={confirmBtnRef} className="ccp3-btn ccp3-btn-confirm" onClick={handleConfirmClick} >Confirm</button>
            :<button ref={confirmBtnRef} className="ccp3-btn ccp3-btn-confirm" onClick={() => { navigate("/payment") }} >Buy Credits</button>
          }
        </div>
      </div>
    </div>
  );
};

export default CreditConfirmPopup;
